import React from "react";
import { Chip, Tooltip } from "@mui/material";
import moment from "moment";
import { PreferredAirline } from "./AirlineRestrictionFields";

const DAY_NAMES = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

export const formatDisplayDate = (value?: string): string => {
    if (!value) return "-";
    const parsed = moment(value, "DD-MMM-YYYY", true);
    if (!parsed.isValid()) return value;
    return parsed.format("DD MMM YYYY");
};

export const formatRouteDisplay = (origin?: string, destination?: string): string => {
    const from = origin || "-";
    const to = destination || "-";
    return `${from} → ${to}`;
};

export const formatTravelDatesDisplay = (start?: string, end?: string): string => {
    return `${formatDisplayDate(start)} → ${formatDisplayDate(end)}`;
};

export const formatRepeatDisplay = (repeats?: any[]): string => {
    if (!Array.isArray(repeats) || !repeats.length) return "-";
    const normalized = repeats.map((r) => String(r || "").toUpperCase()).filter(Boolean);
    if (!normalized.length || (normalized.length === 1 && !normalized[0])) return "-";
    if (normalized.includes("NEVER")) return "Never";
    if (normalized.includes("EVERYDAY")) return "Everyday";
    if (normalized.some((r) => DAY_NAMES.includes(r))) return "Selected Days";
    return normalized[0] || "-";
};

export const PaxTypesDisplay = ({ types }: { types?: string[] }) => {
    const list = Array.isArray(types) ? types.filter(Boolean) : [];
    if (!list.length) return <span className="sunquestEmpty">-</span>;

    if (list.length <= 2) {
        return (
            <span className="sunquestPaxChips">
                {list.map((pax) => (
                    <Chip key={pax} size="small" label={pax} className="sunquestPaxChip" />
                ))}
            </span>
        );
    }

    return (
        <Tooltip title={list.join(", ")} arrow placement="top">
            <Chip
                size="small"
                label={`${list.length} restrictions`}
                className="sunquestPaxChip sunquestPaxChipSummary"
            />
        </Tooltip>
    );
};

export const getAirlineSummaryLabel = (search: any): string => {
    if (Array.isArray(search?.preferredAirlines) && search.preferredAirlines.length) {
        const count = search.preferredAirlines.filter((row: PreferredAirline) => row?.airline).length;
        return count ? `Preferred · ${count}` : "-";
    }
    if (Array.isArray(search?.prohibitedAirlines) && search.prohibitedAirlines.length) {
        return `Prohibited · ${search.prohibitedAirlines.length}`;
    }
    return "-";
};

export const getAirlineDetailText = (search: any): string => {
    if (Array.isArray(search?.preferredAirlines) && search.preferredAirlines.length) {
        return search.preferredAirlines
            .filter((row: PreferredAirline) => row?.airline)
            .map((row: PreferredAirline) => {
                const days = Array.isArray(row.days) && row.days.length ? ` (${row.days.join(",")})` : "";
                return `${row.airline}${days}`;
            })
            .join(", ");
    }
    if (Array.isArray(search?.prohibitedAirlines) && search.prohibitedAirlines.length) {
        return search.prohibitedAirlines.join(", ");
    }
    return "";
};

export const AirlinesDisplay = ({ search }: { search: any }) => {
    const label = getAirlineSummaryLabel(search);
    if (label === "-") return <span className="sunquestEmpty">-</span>;

    const detail = getAirlineDetailText(search);
    const isPreferred = label.startsWith("Preferred");

    return (
        <Tooltip title={detail || label} arrow placement="top">
            <span className={`sunquestAirlineSummary ${isPreferred ? "isPreferred" : "isProhibited"}`}>
                {label}
            </span>
        </Tooltip>
    );
};
